// packages/web/src/components/RecordForm.tsx — R2: the GENERIC record form.
// Given a record type KEY (it fetches that type's schema) plus an optional
// existing record (edit mode), it renders one input per field DRIVEN BY the
// field's declared type (text/number/currency/date/datetime/picklist/boolean/
// file) and one picker per relationship (search/select existing records of the
// target type, respecting cardinality one-vs-many). Submitting creates (POST
// /records) or edits (PATCH /records/:id) through the generic records engine,
// then reconciles relationship LINKS. Client validation is derived from the
// schema (required fields; picklist membership is inherent to the <select>).
// R3 wires this into every screen; R2 proves it on a handful of types.
import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  ApiError,
  createRecord,
  updateRecord,
  linkRecord,
  unlinkRecord,
  uploadRecordBlob,
  listAllRecordsByType,
  getRecord,
  getRecordWithLinks,
  getRecordTypeSchema,
  type RecordRow,
  type RecordTypeSchema,
  type SchemaField,
  type SchemaRelationship,
  type LinkRow,
} from '../lib/api';
import { fieldApplies } from '../lib/conditionalFields';
import { useCoverFrameCapture } from '../lib/coverFrameCapture';
import { describeReadFailure } from '../lib/readFailure';
import './RecordForm.css';

interface RecordFormProps {
  tenantId: string;
  typeKey: string;
  /** Edit mode: the record being edited + its existing links (for relationship
   * prefill). Omit for create mode. */
  existing?: { record: RecordRow; links: LinkRow[] };
  /** Optional pre-fetched schema (tests pass a mock; live callers omit it and
   * the form fetches). */
  schema?: RecordTypeSchema;
  /** CREATE mode only: seed initial field values (edit mode seeds from the
   * existing record instead). */
  prefillFields?: Record<string, unknown>;
  /** CREATE mode only: seed relationship selections, keyed by relationship key
   * — how a sub-record created from its parent detail pre-points at that
   * parent (edit mode seeds from the existing record's links instead). */
  prefillRels?: Record<string, string[]>;
  /** CREATE mode only — "a record created where you stood should land where
   * you stood" (Huan's live walk, 2026-08-23; CV3 gave captures this rule,
   * creation never had it). The screen the create was launched FROM: any
   * cardinality-one relationship on this type whose target matches the
   * context's type is pre-selected with it — and the dependent-pair fill
   * below then completes the rest (a site context fills the client too). */
  contextRecord?: { typeKey: string; id: string };
  /** Controlled fields (2026-08-23): rendered read-only with their seeded
   * value — the server's number ledger owns them, a person never types
   * one. Validation treats them as satisfied by their seed. */
  lockedFields?: string[];
  onSaved: (record: RecordRow) => void;
  onCancel?: () => void;
  /** S2 (additive, optional): the assist bar's fill. When `nonce` changes,
   * `fields` merge into the form's values — but ONLY into fields that are
   * still empty ('' / null / undefined). Nothing a person has typed (or an
   * existing record already holds) is ever overwritten — that rule is the
   * whole point of the assist strip. Boolean and file fields are never
   * assist-filled (an untouched checkbox is indistinguishable from a chosen
   * one, and a file ref can't ride a transcript). */
  assistFill?: { fields: Record<string, unknown>; nonce: number };
  /** S2 (additive, optional): intercept a failed save BEFORE the generic
   * error line renders — how the authority gate's 409 conversion offer
   * (C5e) reaches a caller that knows how to turn it into an ask. Return
   * true when handled; the form then shows no error of its own. */
  onSaveError?: (err: unknown) => boolean;
  /** ⚑ THE EDITS THIS SHEET IS HOLDING BUT HAS NOT SAVED — the Day 2 walk's
   * S3.14, second half.
   *
   * The edit sheet renders this form and `RecordActions` side by side, and a
   * person reads them as ONE sheet: they type the outcome, press Passed, and
   * expect the read to move. It did not — the State row transitions the record
   * AS LOADED, so the server saw no outcome and the move was refused for a
   * field that was on the screen. "Press Save first, reopen, then press
   * Passed" is not a rule anybody can be expected to know, and nothing on the
   * screen said it.
   *
   * So the form publishes what it is holding into this ref on every keystroke,
   * and the State row sends it WITH the move. A ref and not a callback prop
   * deliberately: the parent must not re-render on every character typed, and
   * nothing reads this except at the moment a button is pressed.
   *
   * ⚠ FIELDS ONLY. Relationship changes still need Save — they are separate
   * writes (link/unlink), not part of a transition's payload. */
  pendingFields?: { current: Record<string, unknown> };
}

/** A relationship's currently-selected target ids, keyed by relationship key. */
type RelState = Record<string, string[]>;

function initialFieldValue(field: SchemaField, existing: Record<string, unknown> | undefined): unknown {
  if (existing && field.name in existing) return existing[field.name];
  if (field.default !== undefined) return field.default;
  return field.type === 'boolean' ? false : '';
}

/** Links whose relationship is declared on THIS record (src = this record) —
 * the ones a relationship picker owns. Keyed by relationship (link_type). */
function seedRelationships(recordId: string | undefined, links: LinkRow[]): RelState {
  const out: RelState = {};
  if (!recordId) return out;
  for (const l of links) {
    if (l.src_id !== recordId) continue;
    (out[l.link_type] ??= []).push(l.dst_id);
  }
  return out;
}

/** Coerces a field's string form value to the JSON the engine expects. */
function coerce(field: SchemaField, raw: unknown): unknown {
  switch (field.type) {
    case 'boolean':
      return Boolean(raw);
    case 'number':
    case 'currency':
      if (raw === '' || raw === null || raw === undefined) return undefined;
      return Number(raw);
    default:
      return raw === '' ? undefined : raw;
  }
}

/** What this form would WRITE right now — the save's payload, and the same
 * payload the State row beside it sends with a transition (see
 * `pendingFields`). Module-level so both readers are the one function rather
 * than two that agree until somebody edits one. */
function fieldsPayloadFrom(
  schema: RecordTypeSchema,
  values: Record<string, unknown>,
  existing: { record: RecordRow; links: LinkRow[] } | undefined
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of schema.fields) {
    const c = coerce(f, values[f.name]);
    if (c !== undefined) {
      out[f.name] = c;
      continue;
    }
    // ── ⚑ EMPTYING A FIELD IS AN EDIT (Day 2 walk, S4.9). ──
    //
    // `coerce` answers undefined for an empty input, and an undefined value
    // was simply left out of the payload — so PATCH merged nothing, the old
    // value survived, and the screen said nothing at all while showing the
    // number the person had just deleted. On a record about a child's working
    // day — wardrobe, a parking note, a call time — that is a screen quietly
    // disagreeing with the family about what they told it.
    //
    // A field that HELD something and now holds nothing is sent as null, the
    // engine's own word for "cleared". A REQUIRED field cleared this way is
    // then refused loudly and by name ("required field 'x' cannot be cleared")
    // instead of silently ignored, which is the right half of the same fix.
    //
    // ⚠ EDIT MODE ONLY. On create there is nothing to clear: an empty optional
    // field is simply not set, exactly as before.
    if (!existing) continue;
    const had = existing.record.fields[f.name];
    if (had === undefined || had === null || had === '') continue;
    out[f.name] = null;
  }
  return out;
}

export function RecordForm(props: RecordFormProps) {
  const { tenantId, typeKey, existing, onSaved, onCancel } = props;
  const [schema, setSchema] = useState<RecordTypeSchema | null>(props.schema ?? null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [rels, setRels] = useState<RelState>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<Set<string>>(new Set());

  // Fetch schema if not provided.
  useEffect(() => {
    if (props.schema) {
      setSchema(props.schema);
      return;
    }
    let alive = true;
    getRecordTypeSchema(tenantId, typeKey)
      .then((s) => alive && setSchema(s))
      .catch((e) => alive && setSchemaError(e instanceof ApiError ? e.message : 'Could not load the form.'));
    return () => {
      alive = false;
    };
  }, [tenantId, typeKey, props.schema]);

  // Seed field values + relationship selections once the schema is known. Edit
  // mode seeds from the existing record + its links; create mode seeds from the
  // optional prefill (else field defaults / empty).
  useEffect(() => {
    if (!schema) return;
    const seedSource = existing?.record.fields ?? props.prefillFields;
    const seeded: Record<string, unknown> = {};
    for (const f of schema.fields) seeded[f.name] = initialFieldValue(f, seedSource);
    setValues(seeded);
    const seededRels = existing
      ? seedRelationships(existing.record.id, existing.links)
      : { ...(props.prefillRels ?? {}) };
    if (!existing && props.contextRecord) {
      for (const r of schema.relationships) {
        if (r.targetType !== props.contextRecord.typeKey) continue;
        if ((seededRels[r.key] ?? []).length > 0) continue; // explicit prefill wins
        seededRels[r.key] = [props.contextRecord.id];
        break; // one relationship per context — the first declared match
      }
    }
    setRels(seededRels);
  }, [schema, existing]);

  // S2: apply an assist fill (see the prop's own doc). Declared AFTER the
  // seeding effect above so a fill arriving with the schema still lands on
  // top of the seeded values, never underneath them.
  useEffect(() => {
    const fill = props.assistFill;
    if (!fill || !schema) return;
    setValues((v) => {
      const next = { ...v };
      for (const f of schema.fields) {
        if (f.type === 'boolean' || f.type === 'file') continue;
        if (!(f.name in fill.fields)) continue;
        const cur = next[f.name];
        if (cur !== '' && cur !== null && cur !== undefined) continue;
        const incoming = fill.fields[f.name];
        if (incoming !== undefined && incoming !== null && incoming !== '') next[f.name] = incoming;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.assistFill?.nonce, schema]);

  function setField(name: string, value: unknown) {
    setValues((v) => ({ ...v, [name]: value }));
  }

  // ------------------------------------------------------------------
  // DEPENDENT PICKERS (Huan's walk ruling, 2026-08-23): when two
  // cardinality-one pickers' target types are themselves linked one-to-one
  // (a site carries its client), picking the finer one FILLS the coarser
  // (site -> its client), and picking the coarser FILTERS the finer
  // (client -> only its sites). Entirely schema-derived — the pairs come
  // from the TARGET types' own declared relationships, so this component
  // still knows no pack's nouns.
  // ------------------------------------------------------------------
  interface DepPair {
    /** The finer picker (e.g. wo_site). */
    srcRelKey: string;
    /** The coarser picker it determines (e.g. wo_client). */
    dstRelKey: string;
    /** The link key on the finer TARGET type that names the coarser record
     * (e.g. hvac_site's own `client`). */
    viaKey: string;
  }
  const [depPairs, setDepPairs] = useState<DepPair[]>([]);
  /** dstRelKey-selection -> the allowed id set for its srcRel picker; a
   * missing key means unconstrained. */
  const [allowedByRel, setAllowedByRel] = useState<Record<string, Set<string>>>({});

  useEffect(() => {
    if (!schema) return;
    const ones = schema.relationships.filter((r) => r.cardinality === 'one');
    if (ones.length < 2) return;
    let alive = true;
    (async () => {
      const targetSchemas = new Map<string, RecordTypeSchema>();
      for (const r of ones) {
        if (targetSchemas.has(r.targetType)) continue;
        try {
          targetSchemas.set(r.targetType, await getRecordTypeSchema(tenantId, r.targetType));
        } catch {
          // A target type whose schema will not load simply forms no pairs.
        }
      }
      const pairs: DepPair[] = [];
      for (const a of ones) {
        const ts = targetSchemas.get(a.targetType);
        if (!ts) continue;
        for (const b of ones) {
          if (a.key === b.key) continue;
          const via = ts.relationships.find(
            (x) => x.cardinality === 'one' && x.targetType === b.targetType
          );
          if (via) pairs.push({ srcRelKey: a.key, dstRelKey: b.key, viaKey: via.key });
        }
      }
      if (alive) setDepPairs(pairs);
    })();
    return () => {
      alive = false;
    };
  }, [schema, tenantId]);

  /** The picker onChange, with the dependent FILL: picking the finer record
   * looks up its own via-link and sets the coarser picker to match. The
   * coarser value is overwritten deliberately — with the finer chosen, the
   * coarser is DETERMINED (a site has exactly one client), and the change
   * is visible in the select it lands in. */
  function setRelWithDependents(relKey: string, ids: string[]) {
    setRels((s) => ({ ...s, [relKey]: ids }));
    const pairs = depPairs.filter((p) => p.srcRelKey === relKey);
    if (pairs.length === 0 || ids.length !== 1) return;
    const id = ids[0]!;
    void getRecordWithLinks(tenantId, id)
      .then(({ links }) => {
        for (const pair of pairs) {
          const via = links.find((l) => l.link_type === pair.viaKey && l.src_id === id);
          if (via) setRels((s) => ({ ...s, [pair.dstRelKey]: [via.dst_id] }));
        }
      })
      .catch(() => undefined); // fill is a courtesy; the pick itself stands
  }

  /** SEEDED selections get the fill too: a create launched from a site's
   * screen arrives with the site pre-selected but no click ever happens —
   * without this, the context path would set the site and never complete
   * the client. Runs when the pairs become known; only ever fills an EMPTY
   * partner, so an explicit prefill is never overwritten. */
  useEffect(() => {
    if (depPairs.length === 0) return;
    for (const pair of depPairs) {
      const src = rels[pair.srcRelKey] ?? [];
      const dst = rels[pair.dstRelKey] ?? [];
      if (src.length !== 1 || dst.length > 0) continue;
      const id = src[0]!;
      void getRecordWithLinks(tenantId, id)
        .then(({ links }) => {
          const via = links.find((l) => l.link_type === pair.viaKey && l.src_id === id);
          if (via) {
            setRels((s) =>
              (s[pair.dstRelKey] ?? []).length > 0 ? s : { ...s, [pair.dstRelKey]: [via.dst_id] }
            );
          }
        })
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depPairs]);

  /** The dependent FILTER: a chosen coarser record narrows the finer picker
   * to records via-linked to it. Reads the coarser record's inbound links
   * once per change; clearing it clears the constraint. */
  useEffect(() => {
    if (depPairs.length === 0) return;
    let alive = true;
    (async () => {
      const next: Record<string, Set<string>> = {};
      for (const pair of depPairs) {
        const chosen = rels[pair.dstRelKey] ?? [];
        if (chosen.length !== 1) continue;
        try {
          const { links } = await getRecordWithLinks(tenantId, chosen[0]!);
          next[pair.srcRelKey] = new Set(
            links.filter((l) => l.link_type === pair.viaKey && l.dst_id === chosen[0]).map((l) => l.src_id)
          );
        } catch {
          // An unreadable constraint constrains nothing rather than hiding everything.
        }
      }
      if (alive) setAllowedByRel(next);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depPairs, JSON.stringify(depPairs.map((p) => rels[p.dstRelKey] ?? []))]);

  /** ⚑ WHAT THE SHEET IS HOLDING, published for the State row beside it —
   * see `pendingFields` on the props. Written into a ref, so typing costs the
   * parent nothing and the only reader is a button press. */
  const pendingFields = props.pendingFields;
  useEffect(() => {
    if (!pendingFields || !schema) return;
    pendingFields.current = fieldsPayloadFrom(schema, values, existing);
  }, [pendingFields, schema, values, existing]);

  if (schemaError) return <div class="rf rf--error" role="alert">{schemaError}</div>;
  if (!schema) return <div class="rf rf--loading">Loading…</div>;

  const isEdit = Boolean(existing);

  function validate(): string[] {
    const miss: string[] = [];
    for (const f of schema!.fields) {
      if (!f.required) continue;
      if (f.type === 'boolean') continue; // a required bool is satisfied by either value
      const v = values[f.name];
      const empty = v === undefined || v === null || v === '';
      const badNumber = (f.type === 'number' || f.type === 'currency') && !empty && Number.isNaN(Number(v));
      if (empty || badNumber) miss.push(f.name);
    }
    return miss;
  }

  const fieldsPayload = (): Record<string, unknown> => fieldsPayloadFrom(schema!, values, existing);

  /** Reconcile relationship links against the record: link newly-selected
   * targets, unlink removed ones (lookup only — master_detail unlinks are
   * refused by the engine and left in place). */
  async function reconcileLinks(recordId: string) {
    const existingLinks = existing?.links ?? [];
    for (const rel of schema!.relationships) {
      const wanted = rels[rel.key] ?? [];
      const currentLinks = existingLinks.filter((l) => l.src_id === recordId && l.link_type === rel.key);
      // Remove deselected FIRST (lookup relationships only — master_detail
      // unlinks are refused by the engine), so a changed cardinality-'one'
      // relationship frees its single slot before the new link is added.
      if (rel.kind === 'lookup') {
        for (const l of currentLinks) {
          if (!wanted.includes(l.dst_id)) await unlinkRecord(tenantId, recordId, l.id);
        }
      }
      // Then add newly selected.
      const currentIds = currentLinks.map((l) => l.dst_id);
      for (const dstId of wanted) {
        if (!currentIds.includes(dstId)) await linkRecord(tenantId, recordId, rel.key, dstId);
      }
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    const miss = validate();
    if (miss.length > 0) {
      setMissing(new Set(miss));
      setError('Fill in the required fields.');
      return;
    }
    setMissing(new Set());
    setBusy(true);
    setError(null);
    try {
      const payload = fieldsPayload();
      let record: RecordRow;
      if (existing) {
        record = await updateRecord(tenantId, existing.record.id, payload);
      } else {
        record = await createRecord(tenantId, typeKey, payload);
      }
      await reconcileLinks(record.id);
      onSaved(record);
    } catch (err) {
      // S2: a caller that understands the failure (the authority gate's
      // conversion offer) takes it whole; the generic line is the fallback.
      if (props.onSaveError?.(err)) return;
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="rf" onSubmit={submit} noValidate>
      <div class="rf__title">
        {isEdit ? 'Edit' : 'New'} {schema.label}
      </div>
      {/* A9 — a field that only applies when another field says so. The
          generic form asks; every pack fact behind the answer stays in
          lib/conditionalFields.ts. See that module's header: this is a
          declaration the manifest should be able to make and cannot. */}
      {schema.fields.filter((f) => fieldApplies(typeKey, f, values)).map((f) => (
        <FieldInput
          key={f.name}
          field={f}
          value={values[f.name]}
          onChange={(v) => setField(f.name, v)}
          tenantId={tenantId}
          invalid={missing.has(f.name)}
          locked={props.lockedFields?.includes(f.name) ?? false}
          allFields={schema.fields}
          onSiblingChange={setField}
        />
      ))}
      {schema.relationships.map((r) => (
        <RelationshipPicker
          key={r.key}
          tenantId={tenantId}
          rel={r}
          selected={rels[r.key] ?? []}
          onChange={(ids) => setRelWithDependents(r.key, ids)}
          allowedIds={allowedByRel[r.key] ?? null}
        />
      ))}
      {error && (
        <div class="rf__error" role="alert" data-testid="rf-error">
          {error}
        </div>
      )}
      <div class="rf__actions">
        {onCancel && (
          <button type="button" class="rf__btn rf__btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
        <button type="submit" class="rf__btn rf__btn--primary" disabled={busy} data-testid="rf-submit">
          {busy ? 'Saving…' : isEdit ? 'Save changes' : `Create ${schema.label}`}
        </button>
      </div>
    </form>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  tenantId,
  invalid,
  locked = false,
  allFields,
  onSiblingChange,
}: {
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
  tenantId: string;
  invalid: boolean;
  locked?: boolean;
  /** P2 — every field on this record type, so a `file` field can find out
   * whether a sibling `cover_frame_ref` exists before offering to extract
   * one. Generic on purpose: nothing here names `aa_media`. */
  allFields?: SchemaField[];
  /** P2 — write a SIBLING field's value from inside this one's control (the
   * cover-frame extractor sets `cover_frame_ref` from the video picker).
   * `setField` itself, passed straight through. */
  onSiblingChange?: (name: string, value: unknown) => void;
}) {
  const id = `rf-${field.name}`;
  const cls = `rf__field${invalid ? ' rf__field--invalid' : ''}`;
  // A LOCKED field renders as what it is: an assigned value, not an input.
  // The value still submits with the form; only the typing is gone.
  if (locked) {
    return (
      <div class={cls}>
        <label class="rf__label" for={id}>
          {field.label}
        </label>
        <input
          id={id}
          class="rf__input rf__input--locked"
          type="text"
          value={value === null || value === undefined ? '' : String(value)}
          readOnly
          aria-readonly="true"
          data-testid={`rf-locked-${field.name}`}
        />
        <div class="rf__hint">Assigned automatically.</div>
      </div>
    );
  }
  // ⚑ W3 — A `json` FIELD HAS NO HONEST TEXT INPUT, so it does not get one.
  //
  // Every branch below this line ends at `<input type="text" value={value}>`,
  // and an OBJECT bound to that renders "[object Object]" and saves that
  // string back the moment anybody touches the box. Two of this pack's fields
  // are structured — `aa_review.timeline_marks` and `aa_resume_version.layout`
  // — so opening either record in this form to correct its NAME would have
  // destroyed the thing the record is for. Found while wiring W3's rename
  // door, which is the generic editor by design (no bespoke forms).
  //
  // ⚠ THE VALUE STILL SUBMITS. It is seeded into `values` like every other
  // field and `coerce`'s default branch passes a non-empty value straight
  // through, so saving a rename carries the arrangement across byte-for-byte.
  // Read-only is about the INPUT, never about dropping the data.
  //
  // Rendered through the same shape a controlled field uses (above), because
  // it is the same statement: this value is real, it is yours, and it is not
  // typed here.
  if (field.type === 'json') {
    return (
      <div class={cls}>
        <label class="rf__label" for={id}>
          {field.label}
        </label>
        <input
          id={id}
          class="rf__input rf__input--locked"
          type="text"
          value={value === null || value === undefined || value === '' ? '' : JSON.stringify(value)}
          readOnly
          aria-readonly="true"
          data-testid={`rf-json-${field.name}`}
        />
        <div class="rf__hint">Arranged on its own screen.</div>
      </div>
    );
  }

  const label = (
    <label class="rf__label" for={id}>
      {field.label}
      {field.required && <span class="rf__req" aria-hidden="true"> *</span>}
    </label>
  );

  if (field.type === 'boolean') {
    return (
      <div class="rf__field rf__field--bool">
        <label class="rf__toggle">
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
          />
          <span>{field.label}</span>
        </label>
      </div>
    );
  }

  if (field.type === 'picklist') {
    return (
      <div class={cls}>
        {label}
        <select
          id={id}
          class="rf__input"
          value={(value as string) ?? ''}
          onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
          aria-required={field.required}
        >
          <option value="">{field.required ? 'Select…' : '—'}</option>
          {(field.picklistValues ?? []).map((opt) => (
            <option value={opt} key={opt}>
              {humanizeValue(opt)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'file') {
    return (
      <FileField
        id={id}
        field={field}
        value={value}
        onChange={onChange}
        tenantId={tenantId}
        invalid={invalid}
        allFields={allFields}
        onSiblingChange={onSiblingChange}
      />
    );
  }

  const inputType =
    field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : field.type === 'number' || field.type === 'currency' ? 'number' : 'text';

  return (
    <div class={cls}>
      {label}
      <input
        id={id}
        class="rf__input"
        type={inputType}
        inputMode={field.type === 'currency' || field.type === 'number' ? 'decimal' : undefined}
        step={field.type === 'currency' ? '0.01' : field.type === 'number' ? 'any' : undefined}
        value={(value as string | number | undefined) ?? ''}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        aria-required={field.required}
      />
    </div>
  );
}

// P2 — a coordinator on a set's own LTE will wait for a heavy reel; the
// warning is soft (F0's "no refusal that cannot change gets a retry button"
// has a cousin here: nothing here BLOCKS a send). 150 MB is comfortably past
// an ordinary two-minute reel and comfortably short of the self-tape ceiling
// this pack already accepts elsewhere — a UX threshold, not a plan or a
// price, so it lives here rather than behind an entitlement.
const HEAVY_FILE_WARNING_BYTES = 150 * 1024 * 1024;

function FileField({
  id,
  field,
  value,
  onChange,
  tenantId,
  invalid,
  allFields,
  onSiblingChange,
}: {
  id: string;
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
  tenantId: string;
  invalid: boolean;
  allFields?: SchemaField[];
  onSiblingChange?: (name: string, value: unknown) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [heavy, setHeavy] = useState(false);
  const cls = `rf__field${invalid ? ' rf__field--invalid' : ''}`;

  // P2 (front door §7, decision `cd61d1de`) — THE COVER FRAME.
  //
  // A field is offered the extractor when TWO things are both true: this
  // isn't the cover field itself, and a sibling `cover_frame_ref` file field
  // exists on THIS record type. Nothing here names `aa_media` — any pack
  // that grows the same shape (a video field beside a `cover_frame_ref`
  // field) gets the same behaviour for free, which is the engine's own rule
  // (F0: a vertical's noun never gets hardcoded into shared code).
  const coverSibling =
    field.name !== 'cover_frame_ref' && onSiblingChange
      ? allFields?.find((f) => f.name === 'cover_frame_ref' && f.type === 'file')
      : undefined;

  // B11 — the extraction/preview/scrub STATE and the two acts on it
  // (capture, rescrub) live in one shared hook now, so Materials -> Reels
  // (screens/MaterialsReelsAssist.tsx) never had to copy this pair. What a
  // capture does with the resulting ref stays this field's own business —
  // write it onto the sibling FORM FIELD, unsaved until the record itself
  // saves (see the hook's own header for why that is a callback, not a
  // fixed destination).
  const {
    state: cover,
    capture: captureAt,
    rescrub,
    setSeekSeconds,
  } = useCoverFrameCapture(tenantId, (coverRef) => {
    onSiblingChange!('cover_frame_ref', coverRef);
  });

  async function pick(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    setUploading(true);
    setErr(null);
    setHeavy(false);
    const isVideo = file.type.startsWith('video/');
    if (isVideo && file.size > HEAVY_FILE_WARNING_BYTES) setHeavy(true);
    try {
      const { ref } = await uploadRecordBlob(tenantId, file);
      onChange(ref);
    } catch (uErr) {
      setErr(uErr instanceof ApiError ? uErr.message : 'Upload failed.');
      setUploading(false);
      return;
    }
    setUploading(false);
    // The reel is IN either way — extraction is a best-effort second act on
    // top of an upload that already succeeded, never a reason to fail it.
    if (isVideo && coverSibling) await captureAt(file, undefined);
  }

  return (
    <div class={cls}>
      <label class="rf__label" for={id}>
        {field.label}
        {field.required && <span class="rf__req" aria-hidden="true"> *</span>}
      </label>
      <input id={id} class="rf__input" type="file" onChange={pick} disabled={uploading} />
      {uploading && <div class="rf__hint">Uploading…</div>}
      {heavy && (
        <div class="rf__hint" data-testid="rf-heavy-file-warning">
          This is a large file — a coordinator opening it on set may wait a moment for it to load.
          Uploading now.
        </div>
      )}
      {value ? <div class="rf__hint" data-testid="rf-file-ref">Uploaded ✓</div> : null}
      {err && <div class="rf__error" role="alert">{err}</div>}

      {coverSibling && (cover.extracting || cover.previewUrl || cover.extractError) && (
        <div class="rf__cover" data-testid="rf-cover-frame">
          {cover.extracting && <div class="rf__hint">Choosing a cover frame…</div>}
          {cover.extractError && (
            <div class="rf__error" role="alert" data-testid="rf-cover-frame-error">
              {cover.extractError} Attach a still yourself in the {coverSibling.label} field below.
            </div>
          )}
          {cover.previewUrl && (
            <div class="rf__coverpick">
              <img src={cover.previewUrl} alt="Chosen cover frame" class="rf__coverimg" data-testid="rf-cover-frame-preview" />
              {cover.durationSeconds !== null && cover.durationSeconds > 0 && (
                <input
                  type="range"
                  class="rf__coverscrub"
                  data-testid="rf-cover-frame-scrub"
                  min={0}
                  max={cover.durationSeconds}
                  step={0.1}
                  value={cover.seekSeconds}
                  disabled={cover.extracting}
                  onInput={(e) => setSeekSeconds(Number((e.target as HTMLInputElement).value))}
                  onChange={(e) => void rescrub(Number((e.target as HTMLInputElement).value))}
                />
              )}
              <div class="rf__hint">This is the still shown before anyone taps play.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// S9 (Huan's walk, seeded 204-WO tenant): this picker used to load ONE
// unpaged listRecordsByType call — the engine's default page, 50 rows,
// oldest first. Two failures on any tenant past 50 records of a target
// type: an existing link whose target sat outside that page had no row to
// render as checked/selected (the stored selection READ AS BLANK — 53 of
// 65 seeded notes), and the other 154 work orders could not be linked at
// all because they were never in the list. The web has no server-side
// record text search to reuse (the only search route is the Actor
// enrichment's Wikidata lookup), so the chooser is the spec's sanctioned
// fallback: page at the engine's own LIST_MAX_LIMIT up to a safety cap
// (the same 200×20 shape as api.ts's listAllRecordsByType, inlined so the
// cap is detectable here), plus a client-side filter box once the list is
// big enough to need one. The current SELECTION never depends on paging:
// a selected id no page returned is resolved by getRecord and always
// renders — and if even that read fails, the raw id renders rather than
// nothing. Past the cap the picker says so instead of quietly hiding rows.
const PICKER_PAGE = 200;
const PICKER_MAX_PAGES = 20;
/** Below this many options a filter box is more furniture than help —
 * keeps small tenants' forms (all of Actor's today) exactly as they were. */
const PICKER_FILTER_THRESHOLD = 12;

/** A stand-in row for a selected id whose record read failed — the
 * selection still renders (as its id), never as blank. */
function fallbackRow(id: string, targetType: string): RecordRow {
  return {
    id,
    tenant_id: '',
    record_type: targetType,
    display_name: id,
    fields: {},
    lifecycle_state: null,
    sensitivity: 'normal',
    status: 'active',
    created_by: null,
    created_at: '',
    updated_at: '',
  };
}

function RelationshipPicker({
  tenantId,
  rel,
  selected,
  onChange,
  allowedIds,
}: {
  tenantId: string;
  rel: SchemaRelationship;
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Dependent-pair constraint (see RecordForm): when non-null, only these
   * ids are offered — the current selection always still renders. */
  allowedIds?: Set<string> | null;
}) {
  const [options, setOptions] = useState<RecordRow[] | null>(null);
  /** true when paging hit the safety cap — rows beyond it exist unseen. */
  const [truncated, setTruncated] = useState(false);
  /** Selected ids resolved OUTSIDE the loaded pages: null = fetch in
   * flight; a row = resolved (or the id-only fallback). Once resolved a
   * row stays in the option set even if deselected, so it can be re-picked
   * without another fetch. */
  const [resolved, setResolved] = useState<Record<string, RecordRow | null>>({});
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const id = `rf-rel-${rel.key}`;

  useEffect(() => {
    let alive = true;
    (async () => {
      // v1.1.3 — the wave-parallel loader (R-9) replaces the sequential
      // page loop: same 4,000-row ceiling, a fraction of the round trips.
      // listAll stops on a short page, so a full-to-the-cap result means
      // exactly what the old `cap` flag meant: there may be more.
      const all = await listAllRecordsByType(tenantId, rel.targetType);
      if (!alive) return;
      setOptions(all);
      setTruncated(all.length >= PICKER_PAGE * PICKER_MAX_PAGES);
    })().catch((e) => {
      if (!alive) return;
      // S15 backstop (readFailure.ts): a role/capability refusal from the
      // engine (e.g. a child's role reading a staff-only lookup like
      // Performer or Casting Office) must never surface the engine's own
      // sentence — this picker had never been wired to the backstop that
      // every other read surface already uses.
      if (e instanceof ApiError) {
        const said = describeReadFailure(e);
        setErr(`${said.headline} ${said.detail}`);
        return;
      }
      setErr('Could not load options.');
    });
    return () => {
      alive = false;
    };
  }, [tenantId, rel.targetType]);

  // Resolve any SELECTED id the loaded pages don't hold. Guarded by the
  // `resolved` ledger (an id is entered as null the moment its fetch is
  // launched), so the effect settles instead of looping.
  useEffect(() => {
    if (!options) return;
    const known = new Set(options.map((o) => o.id));
    const missing = selected.filter((sid) => !known.has(sid) && !(sid in resolved));
    if (missing.length === 0) return;
    let alive = true;
    setResolved((r) => {
      const next = { ...r };
      for (const sid of missing) next[sid] = null;
      return next;
    });
    Promise.all(
      missing.map(async (sid) => {
        try {
          return await getRecord(tenantId, sid);
        } catch {
          return fallbackRow(sid, rel.targetType);
        }
      })
    ).then((rows) => {
      if (!alive) return;
      setResolved((r) => {
        const next = { ...r };
        for (const row of rows) next[row.id] = row;
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [options, selected, resolved, tenantId, rel.targetType]);

  /** Every row the picker knows: the loaded pages plus every id resolved
   * individually (deduped — a resolved row also present in a page keeps
   * its page copy). */
  const opts = useMemo(() => {
    const base = options ?? [];
    const known = new Set(base.map((o) => o.id));
    const extra: RecordRow[] = [];
    for (const row of Object.values(resolved)) {
      if (row && !known.has(row.id)) extra.push(row);
    }
    // Alphabetical, always (Huan's walk ruling, 2026-08-23) — the engine
    // returns oldest-first, which reads as random in a dropdown.
    return [...base, ...extra].sort((x, y) =>
      (x.display_name || x.id).localeCompare(y.display_name || y.id, undefined, { sensitivity: 'base' })
    );
  }, [options, resolved]);

  /** The rows actually offered: the filter narrows, but the CURRENT
   * selection always renders — filtered out or not, loaded or not (an id
   * still resolving renders as itself until its name arrives). */
  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // The dependent-pair constraint narrows FIRST; the text filter narrows
    // within it; the current selection is re-added below regardless.
    const constrained = allowedIds ? opts.filter((o) => allowedIds.has(o.id)) : opts;
    const base = q
      ? constrained.filter((o) => (o.display_name || o.id).toLowerCase().includes(q))
      : constrained;
    const present = new Set(base.map((o) => o.id));
    const head: RecordRow[] = [];
    for (const sid of selected) {
      if (present.has(sid)) continue;
      head.push(opts.find((o) => o.id === sid) ?? fallbackRow(sid, rel.targetType));
    }
    return [...head, ...base];
  }, [opts, filter, selected, rel.targetType, allowedIds]);

  if (err) return <div class="rf__field rf__error" role="alert">{err}</div>;

  const filterable = opts.length > PICKER_FILTER_THRESHOLD || truncated;
  const filterBox = filterable ? (
    <input
      id={`${id}-filter`}
      class="rf__input rf__relfilter"
      type="search"
      placeholder="Filter"
      aria-label={`Filter ${rel.label}`}
      value={filter}
      onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
    />
  ) : null;
  const truncatedNote = truncated ? (
    <div class="rf__hint" role="status" data-testid={`rf-rel-${rel.key}-truncated`}>
      Only the first {opts.length.toLocaleString()} records are listed.
    </div>
  ) : null;

  if (rel.cardinality === 'many') {
    return (
      <fieldset class="rf__field rf__field--rel">
        <legend class="rf__label">{rel.label}</legend>
        {filterBox}
        {visible.length === 0 ? (
          <div class="rf__hint">
            {opts.length === 0 ? `No ${rel.targetType} records yet.` : 'No matches.'}
          </div>
        ) : (
          <div class="rf__checks">
            {visible.map((o) => (
              <label class="rf__check" key={o.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(o.id)}
                  onChange={(e) => {
                    const on = (e.target as HTMLInputElement).checked;
                    onChange(on ? [...selected, o.id] : selected.filter((x) => x !== o.id));
                  }}
                />
                <span>{o.display_name || o.id}</span>
              </label>
            ))}
          </div>
        )}
        {truncatedNote}
      </fieldset>
    );
  }

  return (
    <div class="rf__field rf__field--rel">
      <label class="rf__label" for={id}>
        {rel.label}
      </label>
      {filterBox}
      <select
        id={id}
        class="rf__input"
        value={selected[0] ?? ''}
        onChange={(e) => {
          const v = (e.target as HTMLSelectElement).value;
          onChange(v ? [v] : []);
        }}
      >
        <option value="">—</option>
        {visible.map((o) => (
          <option value={o.id} key={o.id}>
            {o.display_name || o.id}
          </option>
        ))}
      </select>
      {truncatedNote}
    </div>
  );
}

/** Turns a picklist raw value ('screen_test') into a readable option label. */
function humanizeValue(v: string): string {
  return v
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
